/**
 * Tax Service
 * 
 * Handles tax calculation by calling backend APIs.
 * Provides clean interface for tax operations in the frontend.
 */

import api from '../services/api';

/**
 * Tax service configuration
 */
const TAX_CONFIG = {
  // API endpoints (resolved against api instance baseURL '/api/v1')
  CALCULATE_ENDPOINT: 'tax/calculate',
  RATE_ENDPOINT: 'tax/rate',
  
  // Transaction types
  TRANSACTION_TYPES: {
    MESSAGE_PURCHASE: 'MESSAGE_PURCHASE',
    POST_PURCHASE: 'POST_PURCHASE',
    SUBSCRIPTION: 'SUBSCRIPTION',
    TIP: 'TIP'
  },
  
  // Default transaction type
  DEFAULT_TRANSACTION_TYPE: 'MESSAGE_PURCHASE'
};

/**
 * Tax Service Class
 */
class TaxService {
  /**
   * Calculate tax for a transaction
   */
  async calculateTax(amount, location, transactionType = TAX_CONFIG.DEFAULT_TRANSACTION_TYPE) {
    try {
      const requestData = {
        amount: parseFloat(amount),
        country: location.country,
        state: location.state,
        postalCode: location.postalCode,
        city: location.city,
        transactionType: transactionType
      };

      const response = await api.post(TAX_CONFIG.CALCULATE_ENDPOINT, requestData);
      
      if (response.data.success) {
        return {
          baseAmount: response.data.amount,
          taxAmount: response.data.taxAmount,
          totalAmount: response.data.totalAmount,
          taxPercentage: response.data.taxPercentage,
          breakdown: response.data.breakdown || [],
          location: {
            country: response.data.country,
            state: response.data.state,
            postalCode: response.data.postalCode
          },
          isFallback: response.data.isFallback || false
        };
      } else {
        throw new Error(response.data.error || 'Tax calculation failed');
      }
    } catch (error) {
      console.error('Tax calculation error:', error);
      throw new Error('Failed to calculate tax: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Get tax rate for a location
   */
  async getTaxRate(location, transactionType = TAX_CONFIG.DEFAULT_TRANSACTION_TYPE) {
    try {
      const params = {
        country: location.country,
        state: location.state,
        postalCode: location.postalCode,
        city: location.city,
        transactionType: transactionType
      };

      const response = await api.get(TAX_CONFIG.RATE_ENDPOINT, { params });
      
      if (response.data.success) {
        return {
          taxPercentage: response.data.taxPercentage,
          taxName: response.data.taxName,
          location: {
            country: response.data.country,
            state: response.data.state,
            postalCode: response.data.postalCode,
            city: response.data.city
          },
          source: response.data.source,
          transactionType: response.data.transactionType
        };
      } else {
        throw new Error(response.data.error || 'Failed to get tax rate');
      }
    } catch (error) {
      console.error('Tax rate error:', error);
      throw new Error('Failed to get tax rate: ' + (error.message || 'Unknown error'));
    }
  }
}

/**
 * Create tax service instance
 */
const taxService = new TaxService();

/**
 * Utility functions for backward compatibility and convenience
 */

/**
 * Get user's location for tax calculation
 */
export function getUserLocation() {
  const savedLocation = localStorage.getItem('userLocation');
  if (savedLocation) {
    try {
      return JSON.parse(savedLocation);
    } catch (e) {
      console.warn('Invalid saved location data');
    }
  }
  
  // Default location
  return {
    country: 'US',
    state: 'CA',
    postalCode: null
  };
}

/**
 * Set user location for tax calculation
 */
export function setUserLocation(location) {
  try {
    localStorage.setItem('userLocation', JSON.stringify(location));
    return true;
  } catch (e) {
    console.error('Failed to save user location:', e);
    return false;
  }
}

/**
 * Calculate tax using backend service
 */
export async function calculateTax(baseAmount, location = null, options = {}) {
  if (!baseAmount || baseAmount <= 0) {
    return {
      baseAmount: 0,
      taxAmount: 0,
      totalAmount: 0,
      taxPercentage: 0,
      breakdown: []
    };
  }

  const userLocation = location || getUserLocation();
  const transactionType = options.transactionType || TAX_CONFIG.DEFAULT_TRANSACTION_TYPE;

  try {
    return await taxService.calculateTax(baseAmount, userLocation, transactionType);
  } catch (error) {
    console.error('Tax calculation failed:', error);
    // Return zero tax calculation on error
    return {
      baseAmount: parseFloat(baseAmount),
      taxAmount: 0,
      totalAmount: parseFloat(baseAmount),
      taxPercentage: 0,
      breakdown: [],
      location: userLocation,
      error: error.message
    };
  }
}

/**
 * Get tax rate using backend service
 */
export async function getTaxRate(location = null, transactionType = TAX_CONFIG.DEFAULT_TRANSACTION_TYPE) {
  const userLocation = location || getUserLocation();
  
  try {
    const rateInfo = await taxService.getTaxRate(userLocation, transactionType);
    return rateInfo.taxPercentage;
  } catch (error) {
    console.error('Get tax rate failed:', error);
    return 0; // Return 0% tax rate on error
  }
}

/**
 * Format tax breakdown for display
 */
export function formatTaxBreakdown(taxCalculation) {
  const { baseAmount, taxAmount, totalAmount, taxPercentage, location, breakdown } = taxCalculation;
  
  // Use backend breakdown if available, otherwise create simple breakdown
  const displayBreakdown = breakdown && breakdown.length > 0 ? 
    breakdown.map(item => ({
      label: `${item.taxName} (${item.taxPercentage}%)`,
      amount: `$${item.taxAmount.toFixed(2)}`,
      location: item.location
    })) : 
    [{
      label: `Tax (${(taxPercentage * 100).toFixed(1)}%)`,
      amount: `$${taxAmount.toFixed(2)}`
    }];

  return {
    subtotal: `$${baseAmount.toFixed(2)}`,
    taxRate: `${(taxPercentage * 100).toFixed(1)}%`,
    taxAmount: `$${taxAmount.toFixed(2)}`,
    total: `$${totalAmount.toFixed(2)}`,
    location: location ? `${location.state || location.country}` : 'Unknown',
    breakdown: [
      {
        label: 'Subtotal',
        amount: `$${baseAmount.toFixed(2)}`
      },
      ...displayBreakdown,
      {
        label: 'Total',
        amount: `$${totalAmount.toFixed(2)}`,
        isTotal: true
      }
    ]
  };
}

/**
 * Validate tax calculation result
 */
export function validateTaxCalculation(calculation) {
  if (!calculation) {
    return { isValid: false, error: 'No calculation provided' };
  }
  
  const { baseAmount, taxAmount, totalAmount } = calculation;
  
  if (typeof baseAmount !== 'number' || typeof taxAmount !== 'number' || typeof totalAmount !== 'number') {
    return { isValid: false, error: 'Invalid amount types' };
  }
  
  if (baseAmount < 0 || taxAmount < 0 || totalAmount < 0) {
    return { isValid: false, error: 'Negative amounts not allowed' };
  }
  
  const expectedTotal = baseAmount + taxAmount;
  const tolerance = 0.001;
  
  if (Math.abs(totalAmount - expectedTotal) > tolerance) {
    return { isValid: false, error: 'Total amount calculation error' };
  }
  
  return { isValid: true };
}

/**
 * Export tax service instance and utilities
 */
export { taxService };

/**
 * Default export with main functions for backward compatibility
 */
const taxCalculator = {
  calculateTax,
  formatTaxBreakdown,
  validateTaxCalculation,
  getUserLocation,
  setUserLocation,
  getTaxRate,
  taxService
};

export default taxCalculator;